import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { FileInfo } from '@cognite/sdk';
import {
  actions,
  fetchAttachments,
  fetchApprovalFiles,
  fetchPreWorkItems,
  fetchReviewComments,
} from 'redux/reducers/project';
import {
  actions as filesActions,
  fetchFilesByIds,
  fetchPnIDsforAsset,
} from 'redux/reducers/files';
import workStepsSlice, {
  fetchWorkSteps,
  fetchWorkStepsAndRemoveExecution,
} from 'redux/reducers/worksteps';
import { AssetMappingType, PnId, Project } from 'types';
import { RootState, AppDispatch } from 'redux/store';
import { AuthState } from 'redux/reducers/auth';
import { METRICS, METRICS_AREA } from 'config/mixpanelConfig';
import { actions as searchActions } from 'redux/reducers/search';
import useMetrics from './useMetrics';
import { useDrawings } from './useDrawings';

const mapFileInfoToConnectedPnid = (singlePnid: FileInfo): PnId => {
  const { id, name, externalId, metadata } = singlePnid;
  return {
    id: String(id),
    nameInCdf: name,
    externalId,
    version: metadata?.['REVISION'],
    filename: metadata?.['FILE_NAME'],
    description: metadata?.['DOCUMENT_TITLE_2'],
    extendedDescription: metadata?.['DOCUMENT_TITLE_3'],
  };
};

const connectPnidsFromFileIds = (procedureId: string, fileIds: number[]) => {
  return (dispatch: AppDispatch, getState: () => RootState) =>
    dispatch(fetchFilesByIds(fileIds)).then((action) => {
      if (getState().project.project?.id !== procedureId) return;
      if (!fetchFilesByIds.fulfilled.match(action)) return;
      dispatch(
        actions.addConnectedPnids(
          action.payload.map(mapFileInfoToConnectedPnid)
        )
      );
    });
};

export const useProjectLoad = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { copyDrawingsToNewProcedure } = useDrawings();

  const { user } = useSelector<RootState, AuthState>((state) => {
    return state.auth;
  });

  const metrics = useMetrics(METRICS_AREA.PROJECT_LOAD);

  const loadProcedure = (proj: Project, loadFiles = true) => {
    const timer = metrics.start(METRICS.PROJECT_LOAD_TIME, {
      project: proj.id,
      status: proj.status,
    });
    const procedureId = proj.id as string;
    dispatch(workStepsSlice.actions.clearWorkSteps());
    dispatch(actions.selectProject(proj));
    dispatch(
      fetchWorkSteps({
        projectId: procedureId,
        projectStatus: proj.status || 'compilation',
      })
    );
    dispatch(fetchReviewComments(procedureId));
    dispatch(fetchPreWorkItems(procedureId));
    dispatch(fetchAttachments(procedureId));
    dispatch(fetchApprovalFiles(procedureId));
    const listOfFileIds = proj.pnIds?.map((pnid) => Number(pnid.id));
    if (listOfFileIds?.length && loadFiles) {
      dispatch(connectPnidsFromFileIds(procedureId, listOfFileIds));
    }
    dispatch(actions.setIsProcedureLoaded());
    metrics.stop(timer);
  };

  const openProcedure = (proj: Project, editMode: boolean = true) => {
    dispatch(workStepsSlice.actions.clearWorkSteps());
    dispatch(actions.selectProject(proj));
    metrics.track(METRICS.PROJECT_LOAD, {
      site: proj.rootAsset,
      status: proj.status,
      editMode,
    });

    if (editMode) {
      navigate(`/project/${proj.id}`);
    } else {
      navigate(`/project/${proj.id}?viewOnly=true`);
    }
  };

  const copyCreateNewProcedure = (proj: Project) => {
    const newId = uuidv4();
    dispatch(workStepsSlice.actions.clearWorkSteps());
    dispatch(
      fetchWorkStepsAndRemoveExecution({
        projectId: proj.id,
        projectStatus: proj.status || 'compilation',
      })
    );
    dispatch(
      actions.loadAndCreateProject({ procedure: proj, newId, newUser: user })
    );
    const listOfFileIds = proj.pnIds?.map((pnid) => Number(pnid.id));
    if (listOfFileIds) {
      dispatch(fetchFilesByIds(listOfFileIds));
    }
    dispatch(fetchPreWorkItems(proj.id as string));
    copyDrawingsToNewProcedure({ ...proj, id: newId }, {}).then(
      (pnIdsDrawings) => dispatch(actions.setPnidDrawings({ pnIdsDrawings }))
    );
    dispatch(actions.setIsProcedureLoaded());
    dispatch(searchActions.setClearSearch());
    navigate(`/project/${newId}`);
    metrics.track(METRICS.PROJECT_USE_HISTORICAL, {
      site: proj.rootAsset,
      status: proj.status,
    });
  };

  const createNewProcedure = (
    asset: AssetMappingType,
    selectedRootAsset: string
  ) => {
    const newId = uuidv4();
    dispatch(actions.createProject({ asset, user, newId, selectedRootAsset }));
    dispatch(filesActions.clearFiles());
    dispatch(actions.addConnectedPnids([]));
    dispatch((dispatch, getState) => {
      dispatch(fetchPnIDsforAsset(asset.assetId)).then((action) => {
        // Ignore stale fetch results if the user opened another project before P&IDs loaded.
        if (getState().project.project?.id !== newId) return;
        if (!fetchPnIDsforAsset.fulfilled.match(action)) return;

        dispatch(
          actions.addConnectedPnids(
            action.payload.map(mapFileInfoToConnectedPnid)
          )
        );
      });
    });
    dispatch(workStepsSlice.actions.clearWorkSteps());
    dispatch(workStepsSlice.actions.setStatusAsLoaded());
    dispatch(actions.setIsProcedureLoaded());
    dispatch(searchActions.setClearSearch());
    metrics.track(METRICS.PROJECT_CREATE, {
      site: selectedRootAsset,
      status: 'compilation',
    });
    navigate(`/project/${newId}`);
  };

  return {
    loadProcedure,
    openProcedure,
    copyCreateNewProcedure,
    createNewProcedure,
  };
};
